import { assign } from "radash";
import { merge } from "lodash";
import { type StateCreator } from "zustand";
import { TAuthLoginResponseData } from "../../api/auth";
import { TUserDataSchema } from "../../api/user";
import { deleteSubdomainCookie } from "../../utils/cookies";
import { STORE_NAME } from "..";

export type TAuthToken = {
  access_token: string;
  refresh_token: string;
};

export type TAuthStoreState = {
  access_token: string;
  refresh_token: string;
  user: TUserDataSchema | null;
} & Partial<TAuthLoginResponseData>;
export type TAuthStoreActions = {
  setTokenData: (value: TAuthToken) => void;
  updateAuthStore: (payload: RecursivePartial<TAuthStoreState>) => void;
  clearAuthStorage: () => void;
  resetAuthStore: () => void;
};

export type TAuthStore = TAuthStoreState & TAuthStoreActions;

const initialAuthStoreState: TAuthStoreState = {
  user: null,
  auth: undefined,
  role_group: [],
  redirect_url: undefined,
  entity: undefined,
  refresh_token: "",
  access_token: "",
};

const clearPersistedAuthStorage = (clearStoreState = true) => {
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("refresh_token");
  sessionStorage.removeItem("expires_at");

  deleteSubdomainCookie("access_token");
  deleteSubdomainCookie("refresh_token");
  deleteSubdomainCookie("cf_clearance");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("expires_at");
  if (clearStoreState) {
    localStorage.removeItem(STORE_NAME);
  }
  sessionStorage.clear();
};

export const createAuthStore: StateCreator<TAuthStore> = (set) => ({
  ...initialAuthStoreState,

  // ✅ Only call this when setting token
  setTokenData: (value) =>
    set((state) => merge({}, state, { tokenData: value })),

  // Can be used to update tokenData or userData
  updateAuthStore: (payload) =>
    set((state) => assign({ ...state }, payload) as Partial<TAuthStore>),

  clearAuthStorage: clearPersistedAuthStorage,

  resetAuthStore: () => {
    // Clear persisted credentials before resetting in-memory auth state.
    clearPersistedAuthStorage(false);
    set(() => initialAuthStoreState);
    // Zustand persistence writes after `set`; remove that saved state last.
    setTimeout(() => localStorage.removeItem(STORE_NAME), 5);
  },
});
